<!-- MegaMedia.vue -->
<script setup lang="ts">
import ISolveIcon from "@/components/ISolveIcon.vue";
import MpdSwiper from "@/components/MpdSwiper.vue";
import { ref, computed, watch, onMounted, resolveComponent, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useNuxtApp, useWindow, useElementSize, useMounted } from '#imports' // Adjust import paths as necessary
import Button from '@/components/Button.vue'
import NavBlogCard from "@/components/cards/NavBlogCard.vue";

import { vwWithMax } from '@/utility/utility';

// Accessing route and router
const route = useRoute()
const router = useRouter()

// Responsive design hooks
const { isTabletOrMobile } = useWindow()

// Dynamically resolve the BlogCard defaultW
const blogCard = NavBlogCard; // resolveComponent('NavBlogCard') // Ensure you have a BlogCardMega component

// Navigation redirect function
function redirect(path: string) {
  router.push(path)
  console.log('redirect', path)
}

// Define the navigation menu items for blog categories
const MenuItems = ref([
  // {
  //   slug: 'featured',
  //   label: 'Featured',
  //   description: 'Our top selected blog posts',
  // },
  {
    slug: 'blog',
    label: 'Blog & Articles',
    description: 'In-depth articles and insights',
  },
  // {
  //   slug: 'news',
  //   label: 'News',
  //   description: 'Latest updates and news from our company',
  // },
  // {
  //   slug: 'webinars',
  //   label: 'Webinars',
  //   description: 'Upcoming and past webinars',
  // },
  // {
  //   slug: 'e-books',
  //   label: 'E-Books',
  //   description: 'Downloadable e-books and resources',
  // },
])

// Active menu state
const activeMenu = ref<{
  slug: string
  label: string
}>({
  slug: 'featured',
  label: 'Featured',
})

// Access Nuxt app's content and IO utilities
const { $content, $io } = useNuxtApp()

// Computed property to fetch all blog records
const blogs = computed(() => {
  const records = $content.content?.blog?.records
  if (Array.isArray(records) && records.length > 0) {
    return records
  }
  return []
})

// Reactive reference for the displayed blogs
const $blogs = ref([])

async function setBlogs() {

  if(!activeMenu.value) {
    return
  }

  if(!Array.isArray(blogs.value) || blogs.value.length === 0) {
    return
  }

  if (activeMenu.value && activeMenu.value.slug === 'featured') {
    $blogs.value = blogs.value.slice(0, 3)
  } else {
    // This is where we set filters based on the content type
    $blogs.value = blogs.value.slice(0, 3)
  }

  await nextTick();
  if (swiperComponent.value && typeof swiperComponent.value.update === 'function') {
    swiperComponent.value.update();
  }
}

// Watch for changes in the active menu to update the displayed blogs
watch(
  activeMenu,
  async (newVal) => {
    if(Array.isArray(blogs.value) && blogs.value.length > 0) {
      setBlogs()
    }
  },
  {
    immediate: true,
  }
)

watch(blogs, async (blogs) => {
  if(Array.isArray(blogs) && blogs.length > 0) {
    setBlogs()
  }
}, {
  immediate: true
})

// On component mount, set the default active menu
onMounted(() => {
  activeMenu.value = {
    slug: 'featured',
    label: 'Featured',
  }
})

// Swiper references and handlers
const swiper = ref()
const swiperComponent = ref(null)

function onSlideChange() {
  // Handle slide change if needed
}

function onSwiper(swiperInstance: any) {
  // console.log('onSwiper', swiperInstance)
  if (swiperInstance) {
    swiper.value = swiperInstance
    // console.log('onSwiper swiper.value', swiper.value)
  }
}

// Container sizing for responsive design
const containerWidth = ref(0)
const containerSized = ref(false)
const carouselContainer = ref()

watch(
  containerWidth,
  (width) => {
    // console.log('containerWidth', width)
    containerSized.value = width > 0
  },
  {
    immediate: true,
  }
)

watch(
  carouselContainer,
  () => {
    const { width } = useElementSize(carouselContainer)
    watch(
      width,
      (newWidth) => {
        containerWidth.value = newWidth
      },
      {
        immediate: true,
      }
    )
  },
  {
    immediate: true,
  }
)

// Total number of blogs displayed
const totalBlogs = computed(() => $blogs.value.length)

// Pagination controls
function paginate(intent: 'next' | 'prev') {
  console.log("Pagination clicked")
  console.log('swiper', swiper.value)
  console.log('swiperComponent', swiperComponent.value)
  if (swiperComponent.value) {
    if (intent === 'prev') {
      swiperComponent.value.slidePrev()
    }
    if (intent === 'next') {
      swiperComponent.value.slideNext()
    }
  }
}

// Emit function for toggling (used in redirects)
const emit = defineEmits<{
  (e: 'toggle'): void
}>()

// Redirect function for blog posts
function redirectToBlog(slug: string) {
  router.push(`/blog/${slug}`)
  nextTick(() => {
    emit('toggle')
  })
}

const defaultWidth = computed(() => vwWithMax(80, 1300) + "px");
</script>

<template>
  <Section id="mega-media" class="io-mega-popover sec-popover-container popover-media mega-media">
    <Section class="sec-mega-container" w="grid grid-cols-[360px_1fr] g-0">
      
      <!-- Navigation Column -->
      <Column class="column col-navigation" w="gp-1">
        <div class="sec-navigation flex flex-col g-0.5">
          <a
            v-for="item in MenuItems"
            :key="item.slug"
            :class="{ 'active': activeMenu.slug === item.slug }"
            @click="activeMenu = item"
            class="sec-nav-button"
          >
            <div class="wrap">
              <div class="icon flex items-start">
                <ISolveIcon /> <!-- Ensure you have an ISolveIcon component -->
              </div>
              <div class="sec-content">
                <div class="title">{{ item.label }}</div>
                <p class="description">{{ item.description }}</p>
              </div>
            </div>
          </a>
        </div>
        <!-- <div class="sec-actions">
          <div class="wrap flex justify-start gpt-1.5">
            <Button btn="blue" class="gpy-1 gpx-1.5 fs--1 fw-600 flex items-center g-0.5" @click="redirect('/blogs')">
              View All Blogs
            </Button>
          </div>
        </div> -->
      </Column>
      
      <!-- Products/Blogs Column -->
      <Column class="column col-products" w="h-full text-red-500 gpt-1">
        <Section ref="carouselContainer" class="max-w-92% mx-auto w-full h-full">
          <div :style="{ maxWidth: `${containerWidth}px` }" class="wrapper w-full">
            <client-only>
            <MpdSwiper
              v-if="containerSized"
              ref="swiperComponent"
              class="horizontal-carousel blogs-carousel"
              :options="{
                onSlideChange,
                onSwiper,
                slidesPerView: 'auto',
                spaceBetween: 0,
                pagination: true,
                navigation: true,
                loop: true,
                breakpoints: {
                  1600: { slidesPerView: 2, spaceBetween: 0 },
                  1366: { slidesPerView: 2, spaceBetween: 0 },
                  1200: { slidesPerView: 2, spaceBetween: 0 },
                  991: { slidesPerView: 2, spaceBetween: 0 },
                  600: { slidesPerView: 1, spaceBetween: 0 },
                }
              }"
              :slides="$blogs"
              :card-component="blogCard"
              @redirect="redirectToBlog"
            />
            </client-only>
          </div>
        </Section>
      </Column>
    </Section>
  </Section>
</template>

<style lang="scss" scoped>

#mega-media {
  width: v-bind(defaultWidth);

  .btn-learn {
    color:white !important;
    .p-button-label {
      color: white !important;
    }
  }
}

.mega-media {
  .sec-mega-container {
    padding: gr(1);
  }

  .col-navigation {
    .sec-nav-button {
      cursor: pointer;
      padding: 10px;
      border-radius: 5px;
      transition: background 0.1s;
      text-decoration: none;

      &.active {
        background-color: #f6f9fc; // Adjust as needed

        .wrap {
          background-color: #f6f9fc;
        }
      }

      .wrap {
        display: flex;
        align-items: center;

        .icon {
          margin-right: 10px;
        }

        .sec-content {
          .title {
            font-weight: bold;
          }

          .description {
            font-size: 0.9em;
            color: #666;
          }
        }
      }
    }
  }

  .col-products {
    :deep(.sec-mpd-swiper) {
      .swiper {
        cursor: grab;
        .swiper-pagination {
          position: relative;
        }

        .swiper-button-prev {
          transform: translateX(-50%);
          &:after {
            font-size: fs(1.5);
          }

          &:hover {
            &:after {
              color: var(--c-sky-blue);
              transform: translateY(-1px);
            }
            &:active {
              transform: translateX(-50%) translateY(0px);
              &:hover {
                transform: translateX(-50%) translateY(1px);
              }
            }
          }
        }

        .swiper-button-next {
          transform: translateX(50%);
          &:after {
            font-size: fs(1.5);
          }
          &:hover {
            &:after {
              color: var(--c-sky-blue);
              transform: translateY(-1px);
            }
            &:active {
              transform: translateX(50%) translateY(0px);
              &:hover {
                transform: translateX(50%) translateY(1px);
              }
            }
          }
        }
      }
    }

    .carousel-controls {
      .prev-button,
      .next-button {
        background-color: #007bff;
        color: #fff;
        border: none;
        padding: 8px 16px;
        border-radius: 4px;
        cursor: pointer;
        transition: background 0.3s;

        &:hover {
          background-color: #0056b3;
        }
      }

      .pagination-dots {
        /* Swiper pagination is handled automatically */
      }
    }

    .total-blogs {
      font-size: 1em;
      color: #333;
    }
  }
}
</style>